import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import useUrlState from '../../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';
import {
  useMaintenanceTicketsRelayQuery,
  parseStatusFilterParam,
} from '../../../hooks/maintenance/query/V2/useMaintenanceTicketsRelay';
import { useMaintenanceTicketTagsRelayQuery } from '../../../hooks/maintenance/query/V2/useMaintenanceTicketTagsRelay';
import {
  mapMaintenanceTicketsToLegacy,
  mapMaintenanceTagsToLegacy,
} from '../../../hooks/maintenance/query/V2/maintenanceMappers';
import { ticketColumns } from '../../../tableColumns/maintenanceColumns';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { history } from '../../../utils';
import AddIcon from '@mui/icons-material/Add';
import { MovaticFieldGroupSelectFilter } from '../../../components/Redesing/movatic-field-group';
import useOrganization from '../../../hooks/use-organization';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import useExportMaintenanceTickets from '../../../hooks/maintenance/query/useExportMaintenanceTickets';
import { ISystem } from '../../../ts/interface/system.interface';
import OrganizationSystemMultiSelect from '../../../components/system/organization-system-multi-select';
import { ITag } from '../../../ts/interface/problem.interface';

const filterOptions = [
  { value: 'open', label: 'Open', disabled: true },
  { value: 'closed', label: 'Closed' },
  { value: 'canceled', label: 'Canceled' },
];

// Map legacy sort keys to GraphQL sort field names
const sortKeyMap: Record<string, string> = {
  created_on: 'CREATED_ON',
  due_on: 'DUE_ON',
  priority: 'PRIORITY',
  status: 'STATUS',
  assignee: 'ASSIGNEE',
  hardware_number: 'HARDWARE_OLD_ID',
  completed_on: 'COMPLETED_ON',
  canceled_on: 'CANCELED_ON',
  description: 'DESCRIPTION',
};

function OrganizationMaintenanceTicket() {
  const organizationInfo = useOrganization();

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    // Pending (dropdown) selections applied via onApplyFilter.
    statusFilter: [{ label: 'Open', value: 'open' }] as Option[],
    tagFilter: [] as Option[],
    selectedSystem: [] as { value: string; label: string; original: ISystem }[],
    // Applied filters that drive the query and chips.
    status: 'open',
    tags: '',
    system: '',
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  const debouncePatternMaintenance = useSearchDebounce(filters.search);

  const [chips, setChips] = useState<SearchChip[]>([]);

  const { data: tagsDataRaw = [] } = useMaintenanceTicketTagsRelayQuery(
    { organizationId: organizationInfo?.id || undefined },
    { enabled: isComplete }
  );

  const loadedTags = useMemo(() => {
    const mapped = tagsDataRaw ? mapMaintenanceTagsToLegacy(tagsDataRaw) : [];
    return mapped.map((item: ITag) => ({
      label: item.name,
      value: item.name,
    }));
  }, [tagsDataRaw]);

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const field = sortKeyMap[filters.sortConfig?.key || 'created_on'] || 'CREATED_ON';
    const order =
      filters.sortConfig?.direction?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
    return [{ field: field as any, order: order as 'ASC' | 'DESC' }];
  }, [filters.sortConfig]);

  // Parse status filter - supports comma-separated multi-select values.
  // No selection means no status filtering at all.
  const statusFilter = useMemo(
    () => parseStatusFilterParam(filters.status),
    [filters.status]
  );

  // Parse tag filter
  const tagFilter = useMemo(() => {
    return filters.tags ? filters.tags.split(',').filter(Boolean) : undefined;
  }, [filters.tags]);

  // Parse system filter - support comma-separated GIDs
  const systemFilter = useMemo(() => {
    return filters.system || undefined;
  }, [filters.system]);

  const {
    data: ticketsDataRaw,
    count: totalTickets,
    isLoading: loadingTickets,
    isRefetching: fetchingTickets,
    hasMore,
    endCursor,
  } = useMaintenanceTicketsRelayQuery(
    {
      organizationId: organizationInfo?.id || undefined,
      systemId: systemFilter,
      status: statusFilter,
      tags: tagFilter,
      search: debouncePatternMaintenance || undefined,
    },
    {
      limit: filters.sizePerPage,
      sort: sortConfig,
      enabled: isComplete && !!organizationInfo?.id,
      after,
    }
  );

  // Only the current page's rows are shown (server-side pagination).
  const ticketItems = useMemo(
    () => (ticketsDataRaw ? mapMaintenanceTicketsToLegacy(ticketsDataRaw) : []),
    [ticketsDataRaw]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsDataRaw]);

  // Rebuild the chip list from the *applied*, URL-persisted filters that drive
  // the query, so the chips always match the fetched data — after a refresh and
  // when a filter is cleared (no chip). Skip while URL state is still restoring.
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    filters.status
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        const option = filterOptions.find((o) => o.value === value);
        next.push({
          label: 'Status',
          field: 'status',
          value,
          displayValue: option?.label ?? value,
          disabled: false,
        });
      });

    filters.tags
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        next.push({ label: 'Tags', field: 'tags', value, displayValue: value });
      });

    filters.system
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        const system = filters.selectedSystem?.find((s) => s.value === value);
        next.push({
          label: 'System',
          field: 'system',
          value,
          displayValue: system?.label ?? value,
        });
      });

    setChips(next);
  }, [isRestoring, filters.status, filters.tags, filters.system, filters.selectedSystem]);

  const columnsDis = useMemo(() => {
    const unitTitle = '';
    const isNewHardware = true;
    const showSystemColumn = true;
    return ticketColumns(
      unitTitle,
      isNewHardware,
      statusFilter.length > 0 ? statusFilter : ['all'],
      showSystemColumn
    );
  }, [statusFilter]);

  const handleChipsChange = useCallback(
    (
      values: string[],
      list: { label: string; value: string }[],
      metadata: { label: string; value: string }
    ): void => {
      resetPagination();
      setFilters((prevState) => ({
        ...prevState,
        [metadata.value]: values.join(','),
      }));

      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setFilters, resetPagination]
  );

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });

      const chipToDelete = deletedGroup.label.toLowerCase() as
        | 'status'
        | 'tags'
        | 'system';

      resetPagination();
      setFilters((prevState) => ({
        ...prevState,
        [chipToDelete]: '',
      }));

      const deleteChip = {
        status: () => setFilters((prevState) => ({ ...prevState, statusFilter: [] })),
        tags: () => setFilters((prevState) => ({ ...prevState, tagFilter: [] })),
        system: () => setFilters((prevState) => ({ ...prevState, selectedSystem: [] })),
      };

      if (deleteChip[chipToDelete]) {
        deleteChip[chipToDelete]();
      }
    },
    [setFilters, resetPagination]
  );

  const { mutateAsync: exportMaintenanceTickets } = useExportMaintenanceTickets();

  const handleExportData = useCallback(() => {
    CustomAsyncToast({
      promise: () =>
        exportMaintenanceTickets({
          status: filters.status,
          tags: filters.tags,
          search: debouncePatternMaintenance || undefined,
          organizationId: organizationInfo?.id,
          systemId: filters.system,
        }),
      successMessage: () => 'Maintenance tickets exported successfully',
      loadingMessage: 'Exporting maintenance tickets...',
      errorMessage: 'Error exporting maintenance tickets',
    });
  }, [
    exportMaintenanceTickets,
    filters,
    debouncePatternMaintenance,
    organizationInfo?.id,
  ]);

  return (
    <PaginationTable
      dataId="maintenance-tickets"
      totalItems={totalTickets || 0}
      isLoading={loadingTickets || !isComplete || isPaginating}
      isLoadingCache={fetchingTickets}
      hasMore={hasMore}
      noDataSlice
      localSearch={false}
      showFilter
      padding={{ pt: 1, pl: 0, pr: 1 }}
      chips={chips}
      handleChipDelete={handleChipDelete}
      buttons={[
        {
          label: 'Export',
          startIcon: <FileDownloadIcon />,
          onClick: handleExportData,
        },
        {
          label: 'Add',
          onClick: () => history.push('/maintenance/add'),
          startIcon: <AddIcon />,
        },
      ]}
      onApplyFilter={() => {
        if (
          filters.status !==
          filters.statusFilter
            .filter((option) => filterOptions.some((item) => item.label === option.label))
            .map((item) => item.value.toString())
            .join(',')
        ) {
          handleChipsChange(
            [
              ...filters.statusFilter
                .filter((option) =>
                  filterOptions.some((item) => item.label === option.label)
                )
                .map((item) => item.value.toString()),
            ],
            filterOptions,
            { label: 'Status', value: 'status' }
          );
        }

        if (
          filters.tags !==
          filters.tagFilter.map((item) => item.value.toString()).join(',')
        ) {
          handleChipsChange(
            filters.tagFilter.map((item) => item.value.toString()),
            loadedTags,
            { label: 'Tags', value: 'tags' }
          );
        }

        if (
          filters.system !==
          filters.selectedSystem.map((item) => item.value.toString()).join(',')
        ) {
          handleChipsChange(
            filters.selectedSystem.map((item) => item.value.toString()),
            filters.selectedSystem,
            { label: 'System', value: 'system' }
          );
        }
      }}
      spaceFilters={
        <>
          <MovaticFieldGroupSelectFilter
            label={'Status'}
            value={filters.statusFilter}
            onChange={(event: React.SyntheticEvent, newValue: any[]) => {
              setFilters((prevState) => ({
                ...prevState,
                statusFilter: newValue,
              }));
            }}
            options={filterOptions}
          />
          <MovaticFieldGroupSelectFilter
            label={'Tags'}
            value={filters.tagFilter}
            onChange={(event, newValue) => {
              setFilters((prevState) => ({
                ...prevState,
                tagFilter: newValue,
              }));
            }}
            options={loadedTags}
          />

          <OrganizationSystemMultiSelect
            organization_id={organizationInfo.id}
            selectedSystem={filters.selectedSystem}
            onChange={(systems) => {
              setFilters((prevState) => ({
                ...prevState,
                selectedSystem: systems.map((system) => ({
                  label: system?.name,
                  value: system?.gid,
                  original: system,
                })),
              }));
            }}
          />
        </>
      }
      customKey="gid"
      onRowCLick={(id: string) => {
        history.push(`/maintenance/${id}`);
      }}
      sortBy={filters.sortConfig.key}
      onSortChange={(sort) => {
        resetPagination();
        setFilters((prevState) => ({
          ...prevState,
          sortConfig: sort as ISortConfig,
        }));
      }}
      columns={columnsDis as IColumn[]}
      items={ticketItems}
      searchPlaceholder={`Search tickets`}
      page={pageNumber}
      rowsPerPage={filters.sizePerPage}
      onPageChange={(page, newPage) => onPageChange(newPage)}
      onRowsPerPageChange={(pageSizeString) => {
        const pageSize = Number(pageSizeString.target.value);
        resetPagination();
        setFilters((prevState) => ({
          ...prevState,
          sizePerPage: pageSize,
        }));
      }}
      handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
        resetPagination();
        setFilters((prevState) => ({
          ...prevState,
          search: e.target.value,
        }));
      }}
      searchText={filters.search}
    />
  );
}

export default OrganizationMaintenanceTicket;
